import { useEffect, useState } from "react";
import { Target } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtINR } from "@/lib/api";

const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

export const RevenueGoal = ({ monthly, auth, onSaved }) => {
  const [month, setMonth] = useState(monthly[monthly.length - 1].month);
  const row = monthly.find((m) => m.month === month) || monthly[monthly.length - 1];
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(row.target_inr ? String(row.target_inr) : ""); }, [row.month, row.target_inr]);

  const target = row.target_inr || 0;
  const pct = target ? Math.round((row.total_inr / target) * 100) : 0;
  const hit = target > 0 && row.total_inr >= target;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/revenue/targets/${row.month}`, { target_inr: Number(value) || 0 }, auth);
      toast.success(`Target saved for ${label(row.month)}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save target");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="revenue-goal-card">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h3 className="font-['Outfit'] font-bold text-[#0A192F] flex items-center gap-2"><Target className="w-4 h-4 text-[#FF3B3B]" />Monthly goal</h3>
        <select value={row.month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="revenue-goal-month">
          {[...monthly].reverse().map((m) => <option key={m.month} value={m.month}>{label(m.month)}</option>)}
        </select>
      </div>
      <div className="mt-4 flex flex-wrap items-baseline gap-x-3 text-[#0A192F]">
        <span className="text-3xl font-extrabold font-['Outfit']" data-testid="revenue-goal-actual">{fmtINR(row.total_inr)}</span>
        <span className="text-sm text-slate-500" data-testid="revenue-goal-target">{target ? `of ${fmtINR(target)} target` : "No target set yet"}</span>
        {target > 0 && <span className={`ml-auto text-sm font-bold ${hit ? "text-[#00B3A4]" : "text-[#FF3B3B]"}`} data-testid="revenue-goal-percent">{hit ? `Goal reached · ${pct}%` : `${pct}% · ${fmtINR(target - row.total_inr)} to go`}</span>}
      </div>
      <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden" data-testid="revenue-goal-progress">
        <div className={`h-full rounded-full transition-[width] duration-700 ${hit ? "bg-[#00B3A4]" : "bg-[#FF3B3B]"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="mt-4 flex gap-2 max-w-sm">
        <Input type="number" min="0" step="1000" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Target in ₹" className="bg-white" data-testid="revenue-goal-input" />
        <Button onClick={save} disabled={saving || value === ""} className="bg-[#0A192F] text-white" data-testid="revenue-goal-save">{saving ? "Saving…" : "Save target"}</Button>
      </div>
    </div>
  );
};
